import axios from "@utils/axios-instance";
import { isAxiosError } from "axios";
import { getErrorFromBackend } from "@/utils/errors";
import { NO_OUTBOUND_NUMBER_MESSAGE } from "@/models/phone-number.model";
import type { TInteraction } from "@/services/email.service";

/**
 * Sends a text to the lead's own phone from the agent's caller ID
 * (POST /lead/{id}/sms). The idempotency key makes a retried request — a
 * double click, a retry after a 502 — send at most one text.
 */
export const sendLeadSms = async (leadId: number, body: string, idempotencyKey: string): Promise<TInteraction> => {
  const response = await axios.post(`/lead/${leadId}/sms`, { body }, { headers: { "Idempotency-Key": idempotencyKey } });
  return response.data;
};

/** Twilio codes the server passes back on SMS_REFUSED for numbers that can't take texts. */
const OPTED_OUT_CODE = 21610;
const UNREACHABLE_CODES = [21211, 21614];

export type TSmsError = {
  message: string;
  /** The lead can't be texted at all (opted out / unreachable number): no point retrying. */
  blocked: boolean;
  /** The server may or may not have sent it (502 / network): retry with the same key. */
  uncertain: boolean;
};

/** Maps a failed send to a readable message — never a generic failure. */
export const getSmsError = (e: unknown): TSmsError => {
  const fail = (message: string, flags: Partial<Omit<TSmsError, "message">> = {}): TSmsError => ({
    message,
    blocked: false,
    uncertain: false,
    ...flags,
  });

  // The server records a text that may have gone out as unconfirmed, and a retry with the
  // same key never texts the lead twice.
  const unconfirmed = () =>
    fail("The text wasn't confirmed. If it shows as Not confirmed, it may have reached the lead; sending it again won't text them twice.", {
      uncertain: true,
    });

  if (!isAxiosError(e) || !e.response) return unconfirmed();

  const status = e.response.status;
  const error = (e.response.data as { error?: { code?: string; message?: string; details?: { providerCode?: number | null } } })
    ?.error;

  if (status === 409 && error?.code === "NO_CALLER_ID") return fail(NO_OUTBOUND_NUMBER_MESSAGE);
  if (status === 422 && error?.code === "SMS_REFUSED") {
    const providerCode = error.details?.providerCode ?? null;
    const blocked = providerCode === OPTED_OUT_CODE || (providerCode !== null && UNREACHABLE_CODES.includes(providerCode));
    return fail(error.message || "The text was refused by the phone carrier.", { blocked });
  }
  if (status === 422 && error?.code === "VALIDATION_FAILED") {
    return fail("The text must be 1 to 1,600 characters (an emoji counts as two).");
  }
  // The other code-less 422 is the request-content guard; this one means the
  // lead has no phone number that can take a text.
  if (status === 422 && !error?.code && /phone/i.test(error?.message ?? "")) {
    return fail("This lead's number can't receive texts.", { blocked: true });
  }
  // Limits are per agent (10 min) and per company (1 h). Retry-After isn't readable cross-origin.
  if (status === 429) return fail("Too many texts sent recently; try again later.");
  if (status === 502 || status === 504) return unconfirmed();
  return fail(getErrorFromBackend(e));
};

export default {
  sendLeadSms,
};
